import { defineStore } from 'pinia';
import type {
  IUserLimits,
  IBrandLimits,
  IWagerProgressType,
  IRestrictionPermissions,
} from '~/interfaces/dto/auth/response';
import type {
  IRgStore,
  TWagerLimitType,
} from '~/interfaces/stores/rg.store.interface';
import { limitTypes } from '~/assets/data/rgMapping';

const defaultPermissions = {
  allowedPayout: true,
  allowedWithdrawal: true,
  allowedWager: true,
  allowedLogin: true,
};

export const useRgStore = defineStore('rg', {
  state: (): IRgStore => {
    return {
      // config
      brandLimits: null,
      brandLocks: null,
      // locks
      exclusions: null,
      partialRegLock: null,
      currentRestriction: null,
      // session
      sessionLimit: null,
      currentSessionLimit: null,
      currentTimer: null,
      timerSet: false,
      // cooling off
      coolingOffLimit: null,
      currentCoolOff: null,
      // wager limits
      wagerLimits: null,
      currentWagerThresholds: null,
      currentWagerLimits: null,
      // permissions
      restrictionPermissions: {
        allowedPayout: true,
        allowedWithdrawal: true,
        allowedWager: true,
        allowedLogin: true,
      },
      accountLocked: false,
      // withdrawal verification lock
      withdrawalVerificationRequired: false,
    };
  },
  actions: {
    setPermissions(permissions: IRestrictionPermissions) {
      if (!!permissions) {
        this.restrictionPermissions = permissions;
        if (!permissions?.allowedPayout || !permissions?.allowedWager) {
          this.accountLocked = true;
        }
      }
    },
    setBrandLimits(limits: IBrandLimits[]) {
      this.brandLimits = limits;
      this.sessionLimit = limits.find((abl) =>
        abl.name.includes('Session Limit'),
      );
      this.coolingOffLimit = limits.find(
        (abl) =>
          abl.name.toLowerCase().includes('cooling off') ||
          abl.name.toLowerCase().includes('cool off'),
      );
      const wagerLimits = limits.filter((abl) =>
        abl.name.toLowerCase().includes('wager'),
      );
      this.wagerLimits = wagerLimits.reduce((wagerLimitsObj, currentLimit) => {
        const limitType = limitTypes.find((lt) =>
          currentLimit.name.toLowerCase().includes(lt),
        );
        return Object.assign(wagerLimitsObj, { [limitType]: currentLimit });
      }, {}) as Record<TWagerLimitType, IBrandLimits>;
    },
    setUserLimits(limits: IUserLimits[]) {
      const auth = useAuthStore();
      this.setPartialRegLock(!auth?.currentUser?.isRegistrationComplete);
      if (!!limits?.[0]?.restrictionType?.restrictionPermissions)
        this.setPermissions({
          ...limits?.[0]?.restrictionType?.restrictionPermissions,
          ...(this.hasPartialRegLock ? { allowedWithdrawal: false } : {}),
        });

      const wagerLimits = limits.filter((abl) =>
        abl.restrictionType.name.toLowerCase().includes('wager'),
      );
      this.currentWagerThresholds = wagerLimits.reduce(
        (wagerLimitsObj, currentLimit) => {
          const limitType = limitTypes.find((lt) =>
            currentLimit.restrictionType.name.toLowerCase().includes(lt),
          );
          return Object.assign(wagerLimitsObj, { [limitType]: currentLimit });
        },
        {},
      ) as Record<TWagerLimitType, IUserLimits>;

      const sessionLimit = limits.find((abl) =>
        abl.restrictionType.name.includes('Session Limit'),
      );
      if (!!sessionLimit) {
        this.currentSessionLimit = sessionLimit;
        this.currentTimer = this.currentSessionLimit.threshold * 60;
      } else {
        this.currentSessionLimit = null;
        this.currentTimer = null;
      }

      const coolingOffLimit = limits.find(
        (abl) =>
          (abl.restrictionType.name.toLowerCase().includes('cool off') ||
            abl.restrictionType.name.toLowerCase().includes('cooling off')) &&
          abl.isActive,
      );

      if (!!coolingOffLimit) {
        this.currentCoolOff = coolingOffLimit;
      }

      const selfExclusion = limits.find(
        (abl) =>
          abl.restrictionType.name.includes('(Self Exclusion)') && abl.isActive,
      );
      if (!!selfExclusion) this.setUserLocks(selfExclusion);
      else if (!!coolingOffLimit) this.setUserLocks(coolingOffLimit);
    },
    setTimerSet(timerSet: boolean) {
      this.timerSet = timerSet;
    },
    setExclusions(exclusions: IBrandLimits[]) {
      this.exclusions = exclusions;
    },
    setBrandLocks(restrictionArr: IBrandLimits[]) {
      this.brandLocks = restrictionArr;
    },
    setUserLocks(restriction: IUserLimits) {
      const auth = useAuthStore();
      this.currentRestriction = restriction;
      this.setPartialRegLock(!auth?.currentUser?.isRegistrationComplete);
      if (!!restriction) {
        this.setPermissions({
          ...restriction?.restrictionType?.restrictionPermissions,
          ...(this.hasPartialRegLock ? { allowedWithdrawal: false } : {}),
        });
        if (
          restriction?.restrictionType?.mappingCode ===
          'a314caba-6b6a-46f0-8386-5e8502b7e34f'
        )
          this.withdrawalVerificationRequired = true;
      }
    },
    setWithdrawalVerificationLock(lock: boolean) {
      this.withdrawalVerificationRequired = lock;
    },
    setAccountLockStatus(locked: boolean) {
      this.accountLocked = locked;
    },
    setWagerDuration(durations: IWagerProgressType[]) {
      this.currentWagerLimits = durations.reduce(
        (wagerLimitsObj, currentLimit) => {
          const limitType = limitTypes.find((lt) =>
            currentLimit.limitName.toLowerCase().includes(lt),
          );
          return Object.assign(wagerLimitsObj, { [limitType]: currentLimit });
        },
        {},
      ) as Record<TWagerLimitType, IWagerProgressType>;
    },
    setPartialRegLock(val: boolean) {
      const { $enabled } = useNuxtApp();
      if ($enabled('myaccount.enablepartialreg')) this.partialRegLock = val;
      else this.partialRegLock = false;
    },
    removeLocks() {
      this.restrictionPermissions = JSON.parse(
        JSON.stringify(defaultPermissions),
      );
      this.currentRestriction = null;
      this.withdrawalVerificationRequired = false;
    },
  },
  getters: {
    hasRestrictions(state) {
      return !!state.currentCoolOff || !!state.currentRestriction;
    },
    canWager(state) {
      return !!state.restrictionPermissions?.allowedWager;
    },
    hasPartialRegLock(state) {
      const { $enabled } = useNuxtApp();
      if ($enabled('myaccount.enablepartialreg')) return state.partialRegLock;
      else return false;
    },
    hasSelfExclusion(state) {
      const exclusionMappingCodes = state.exclusions?.map(
        (exclusion) => exclusion.mappingCode,
      );
      return (
        (exclusionMappingCodes?.includes(
          state.currentRestriction?.restrictionType?.mappingCode,
        ) &&
          state.currentRestriction?.isActive) ||
        false
      );
    },
    isAccountLocked(state) {
      return (
        !state.restrictionPermissions?.allowedPayout ||
        !state.restrictionPermissions?.allowedWager
      );
    },
  },
});
